import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, User, Tag, Share2, Phone } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useDocumentMeta } from '../lib/useDocumentMeta';

const API = (() => {
  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    if (host === 'smt-services17.fr' || host === 'www.smt-services17.fr' || host.endsWith('.emergent.host')) {
      return `${window.location.origin}/api`;
    }
  }
  return `${process.env.REACT_APP_BACKEND_URL || ''}/api`;
})();

const CATEGORY_LABEL = {
  vitres: 'Vitres', terrasse: 'Terrasse', menage: 'Ménage',
  moquette: 'Moquette', veranda: 'Véranda', local: 'SEO local', general: 'Général',
};

const formatDate = (iso) => {
  try { return new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }); }
  catch { return iso; }
};

const BlogPostPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [related, setRelated] = useState([]);

  // Build an Article JSON-LD once the post is loaded (null otherwise).
  const articleJsonLd = post ? {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: post.title,
    description: post.meta_description || post.excerpt,
    image: post.cover_image ? [post.cover_image] : undefined,
    datePublished: post.published_at,
    dateModified: post.updated_at || post.published_at,
    author: {
      '@type': 'Organization',
      name: post.author || 'SMT Services',
      url: 'https://smt-services17.fr',
    },
    publisher: {
      '@type': 'Organization',
      name: 'SMT Services',
      url: 'https://smt-services17.fr',
      logo: {
        '@type': 'ImageObject',
        url: 'https://smt-services17.fr/logo-smt.png',
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://smt-services17.fr/blog/${post.slug}`,
    },
    keywords: (post.tags || []).join(', '),
    articleSection: post.category,
    inLanguage: 'fr-FR',
  } : null;

  // Dynamic SEO meta — depends on the loaded post.
  // When the post is missing (deleted/404), explicitly noindex the page so
  // Google removes it from its index instead of treating it as a soft-404 /
  // "page avec redirection" (the URL stays the same but content says "introuvable").
  useDocumentMeta({
    title: post
      ? `${post.title} — SMT Services`
      : (error ? 'Article introuvable — SMT Services' : 'Article — Blog SMT Services'),
    description:
      post?.meta_description ||
      "Conseils & guides de nettoyage par SMT Services — pros du nettoyage de vitres, terrasses, moquettes en Charente-Maritime.",
    path: `/blog/${slug}`,
    image: post?.cover_image,
    ogType: 'article',
    jsonLd: articleJsonLd,
    noindex: !!error,
  });

  useEffect(() => {
    setLoading(true);
    setError(null);
    axios.get(`${API}/blog/${slug}`)
      .then((res) => setPost(res.data))
      .catch(() => setError('Article introuvable'))
      .finally(() => setLoading(false));

    axios.get(`${API}/blog`)
      .then((res) => setRelated(res.data.filter((p) => p.slug !== slug).slice(0, 3)))
      .catch(() => setRelated([]));

    window.scrollTo(0, 0);
  }, [slug]);

  const sharePost = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try { await navigator.share({ title: post.title, url }); } catch {}
    } else {
      await navigator.clipboard.writeText(url);
      alert('Lien copié !');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-10 h-10 border-4 border-[hsl(207,90%,54%)] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col">
        <Header />
        <main className="flex-1 flex flex-col items-center justify-center p-8 text-center">
          <h1 className="text-3xl font-bold text-slate-900 mb-4">Article introuvable</h1>
          <p className="text-slate-500 mb-6">Cet article n'existe pas ou n'est plus publié.</p>
          <Link to="/blog" className="bg-[hsl(207,90%,54%)] hover:bg-[hsl(207,90%,44%)] text-white px-6 py-3 rounded-full font-semibold">
            Retour au blog
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Header />
      <main className="flex-1">
        {/* Hero image */}
        <div className="relative h-[50vh] min-h-[400px] w-full overflow-hidden bg-slate-900">
          <img src={post.cover_image} alt={post.title} className="absolute inset-0 w-full h-full object-cover opacity-70" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
          <div className="relative z-10 h-full max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col justify-end pb-10 text-white">
            <Link to="/blog" className="inline-flex items-center gap-2 text-sm font-semibold text-white/85 hover:text-white mb-4">
              <ArrowLeft className="w-4 h-4" /> Retour au blog
            </Link>
            <span className="inline-block bg-white/15 backdrop-blur px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest mb-3 w-fit">
              {CATEGORY_LABEL[post.category] || post.category}
            </span>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mb-4">{post.title}</h1>
            <div className="flex flex-wrap items-center gap-4 text-sm text-white/80">
              <span className="inline-flex items-center gap-1.5"><Calendar className="w-4 h-4" />{formatDate(post.published_at)}</span>
              <span className="inline-flex items-center gap-1.5"><User className="w-4 h-4" />{post.author}</span>
            </div>
          </div>
        </div>

        {/* Content */}
        <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <p className="text-lg text-slate-600 italic leading-relaxed border-l-4 border-[hsl(207,90%,54%)] pl-4 mb-8">
            {post.excerpt}
          </p>

          <div className="prose prose-slate max-w-none prose-headings:font-bold prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4 prose-h3:text-xl prose-h3:mt-6 prose-h3:mb-3 prose-p:leading-relaxed prose-a:text-[hsl(207,90%,44%)] prose-a:font-semibold prose-strong:text-slate-900 prose-li:my-1 prose-table:text-sm prose-th:bg-slate-100 prose-th:px-3 prose-th:py-2 prose-td:px-3 prose-td:py-2 prose-td:border prose-td:border-slate-200 prose-th:border prose-th:border-slate-200">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{post.content}</ReactMarkdown>
          </div>

          {/* Tags + share */}
          <div className="mt-12 pt-8 border-t border-slate-200 flex flex-wrap items-center justify-between gap-4">
            <div className="flex flex-wrap items-center gap-2">
              {post.tags?.map((t) => (
                <span key={t} className="inline-flex items-center gap-1 bg-slate-100 text-slate-600 text-xs font-medium px-2.5 py-1 rounded-full">
                  <Tag className="w-3 h-3" /> {t}
                </span>
              ))}
            </div>
            <button onClick={sharePost} data-testid="share-button" className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white px-5 py-2 rounded-full text-sm font-semibold transition-colors">
              <Share2 className="w-4 h-4" /> Partager
            </button>
          </div>

          {/* CTA */}
          <div className="mt-12 bg-gradient-to-br from-[hsl(207,90%,54%)] to-[hsl(207,90%,44%)] text-white rounded-3xl p-8 sm:p-10 text-center shadow-xl">
            <h3 className="text-xl sm:text-2xl font-bold mb-3">Vous avez besoin d'une intervention ?</h3>
            <p className="opacity-90 mb-6">Devis gratuit sous 24h dans tout le secteur de Chaillevette, Royan et Marennes.</p>
            <div className="flex flex-wrap gap-3 justify-center">
              <button onClick={() => navigate('/#reservation')} className="bg-white text-[hsl(207,90%,44%)] font-bold px-6 py-3 rounded-full shadow hover:shadow-xl transition-all">
                Demander un devis
              </button>
              <a href="tel:+33625684849" className="inline-flex items-center gap-2 bg-[hsl(32,95%,55%)] hover:bg-[hsl(32,95%,45%)] text-white font-bold px-6 py-3 rounded-full transition-colors">
                <Phone className="w-4 h-4" /> 06 25 68 48 49
              </a>
            </div>
          </div>
        </article>

        {/* Related */}
        {related.length > 0 && (
          <section className="bg-slate-50 py-16">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
              <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-8 text-center">À lire aussi</h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {related.map((p) => (
                  <Link key={p.id} to={`/blog/${p.slug}`} className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all hover:-translate-y-0.5 border border-slate-100">
                    <div className="aspect-[16/10] bg-slate-100 overflow-hidden">
                      <img src={p.cover_image} alt={p.title} loading="lazy" className="w-full h-full object-cover" />
                    </div>
                    <div className="p-5">
                      <h3 className="font-bold text-slate-900 line-clamp-2 mb-1">{p.title}</h3>
                      <p className="text-sm text-slate-500 line-clamp-2">{p.excerpt}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default BlogPostPage;
